import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import firebaseInstance from '../lib/firebase';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { ApolloProvider } from '@apollo/client';
import client from '../src/apollo';
import { ConfigContext } from '../lib/config';
import '../styles/globals.css';
import { isPromotionExpired } from '../src/helpers/isPromotionExpired';
import { Config } from '@/lib/interfaces';
import { events, getAnalyticsClient, getUrlParams } from '@/lib/analytics';
import PodsightsScript from '@/components/PodsightsScript';
import { useState } from 'react';
import { GTM_ID, pageview } from '@/lib/gtm';
import Head from 'next/head';
import App, { AppContext, AppProps } from 'next/app';
import { getNonce } from '@/lib/nonce';
import { OTELProvider } from '@/lib/otel-provider';

type PagePropsWithNonce = AppProps['pageProps'] & { __nonce?: string; config?: Config };

function MyApp({ Component, pageProps }: AppProps) {
  const auth = getAuth(firebaseInstance);
  const router = useRouter();
  const config = (pageProps as PagePropsWithNonce).config as Config;
  const [userLoading, setUserLoading] = useState(true);
  const [firebaseUser, firebaseLoading] = useAuthState(auth);

  useEffect(() => {
    if (!firebaseUser && !firebaseLoading) {
      signInAnonymously(auth)
        .finally(() => {
          setUserLoading(false);
        })
        .catch((error: unknown) => {
          console.log('--------> Error attemping sign into Firebase', error);
        });
    }
  }, [auth, firebaseUser, firebaseLoading]);

  useEffect(() => {
    const analytics = getAnalyticsClient();
    const trackPageChange = (url: string) => {
      const { error, message, promotionCode, promotionType, origin } = getUrlParams(window.location.search);
      analytics.track(events.viewPage, {
        url,
        error,
        message,
        promotionCode,
        promotionType,
        origin,
        host: window.location.host,
        pathname: window.location.pathname,
        search: window.location.search,
      });
    };
    router.events.on('routeChangeComplete', trackPageChange);
    router.events.on('routeChangeComplete', pageview);

    return () => {
      // unsubscribe from the event with the `off` method
      router.events.off('routeChangeComplete', trackPageChange);
      router.events.off('routeChangeComplete', pageview);
    };
  }, [router.events, router.query]);

  useEffect(() => {
    if (window.location.pathname.endsWith('/expired')) return;

    const isPreviewMode = router.asPath.includes('_storyblok');

    // Add safety check for config and promotion
    if (config?.promotion?.endDate) {
      const expired = isPromotionExpired(config.promotion.endDate);

      if (expired && !isPreviewMode) {
        window.location.replace('/expired');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!firebaseUser && userLoading) return <></>;

  // Get nonce for CSP (prefer server-provided value to ensure SSR has nonce)
  const nonce = (pageProps as PagePropsWithNonce)?.__nonce ?? getNonce();
  // Disable third-party marketing scripts on localhost to avoid noisy vendor CORS/errors in local dev
  const isLocalhost = typeof window !== 'undefined' && /(^|\.)localhost$/.test(window.location.hostname);
  const enableMarketingScripts = !isLocalhost;

  return (
    <>
      <Head>
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      {enableMarketingScripts && (
        <>
          <Script
            id="gtm-init"
            strategy="afterInteractive"
            nonce={nonce}
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
                // Set global nonce for GTM to use on dynamically created scripts
                window.gtmCspNonce = '${nonce}';
                
                // Override document.createElement to add nonce to all GTM-created scripts
                (function() {
                  const originalCreateElement = document.createElement;
                  document.createElement = function(tagName) {
                    const element = originalCreateElement.apply(this, arguments);
                    if (tagName.toLowerCase() === 'script' && window.gtmCspNonce) {
                      element.setAttribute('nonce', window.gtmCspNonce);
                    }
                    return element;
                  };
                })();
              `,
            }}
          />
          <Script
            id="gtm-src"
            strategy="afterInteractive"
            nonce={nonce}
            src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}&gtm_csp_nonce=${encodeURIComponent(
              String(nonce ?? ''),
            )}`}
          />
        </>
      )}
      <OTELProvider>
        <ApolloProvider client={client}>
          <ConfigContext.Provider value={config ?? null}>
            <Component {...pageProps} />
          </ConfigContext.Provider>
        </ApolloProvider>
      </OTELProvider>
      {enableMarketingScripts && config?.podsightsKey && <PodsightsScript sdkKey={config.podsightsKey} nonce={nonce} />}
    </>
  );
}

export default MyApp;

// Ensure nonce is available during SSR so Next's Script loader can set it on inline scripts
MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  const req = appContext.ctx.req as { headers?: Record<string, string | string[] | undefined> } | undefined;
  const headers = req?.headers || {};
  // Node lowercases header keys, but guard both just in case
  const headerNonce =
    (headers['x-csp-nonce'] as string | undefined) ||
    (headers['X-CSP-Nonce'] as string | undefined) ||
    (headers['x-nonce'] as string | undefined) ||
    (headers['X-Nonce'] as string | undefined);
  const cookieHeader = (headers.cookie as string | undefined) || (headers.Cookie as string | undefined);
  let cookieNonce: string | undefined;
  if (cookieHeader) {
    const match = String(cookieHeader).match(/(?:^|;\s*)csp-nonce=([^;]+)/);
    cookieNonce = match?.[1];
  }
  const nonce = (headerNonce as string) || cookieNonce || undefined;

  appProps.pageProps = {
    ...appProps.pageProps,
    __nonce: nonce,
  };

  return appProps;
};
